import axios from 'axios';
import { saveBidMode } from '../../scenes/BidSession/helpers.js';
import * as actionTypes from './actionTypes';
import apiCall from '../app/apiCall';
import { BID_TYPES } from './../constants/index';
import { buildUrlWithAdminImpersonation } from '../../utilities/helpers';

let commonLabels;
if (sessionStorage.getItem('lng') === 'fr') {
  commonLabels = require('./../../labels/labels.french.json').commonLabels;
} else {
  commonLabels = require('./../../labels/labels.json').commonLabels;
}

export const fetchBidSessionRequest = () => ({
  type: actionTypes.FETCH_BID_SESSION_REQUEST
});
export const fetchBidSessionFailure = message => ({
  type: actionTypes.FETCH_BID_SESSION_FAILURE,
  message
});
export const fetchBidSessionSuccess = bidSession => ({
  type: actionTypes.FETCH_BID_SESSION_SUCCESS,
  bidSession
});
export const clearData = () => ({ type: actionTypes.CLEAR_DATA });
export const fetchmode = bidmode => ({
  type: actionTypes.FETCH_BID_MODE_SUCCESS,
  bidmode
});
export function savePreference(bidPreference) {
  return () => {
    const savePreferenceUrl = buildUrlWithAdminImpersonation('preferences/save');
    return apiCall(savePreferenceUrl, 'post', bidPreference);
  };
}


export function updateAllScores(preferencesWithScores) {
  return () => {
    const updateScoresUrl = buildUrlWithAdminImpersonation('preferences/updateScores');

    const basicPreferences = [];
    preferencesWithScores.forEach(preference => {
      const item = {
        bidSessionId: preference.bidSessionId,
        staffId: preference.staffId,
        employeeBidId: preference.employeeBidId,
        bidAggregateId: preference.bidAggregateId,
        bidSessionSnapshotId: preference.bidSessionSnapshotId,
        preferenceOptionId: preference.preferenceOptionId,
        preferenceOptionPropertyId: preference.preferenceOptionPropertyId,
        score: preference.score
      };
      basicPreferences.push(item);
    });
    const data = {
      preferences: basicPreferences
    };
    return apiCall(updateScoresUrl, 'post', data);
  };
}

export function orderPreferences(preferences) {
  function compare(a, b) {
    if (a.score < b.score) {
      return -1;
    }
    if (a.score > b.score) {
      return 1;
    }
    return 0;
  }
  preferences.entries = preferences.entries.sort(compare);
  return preferences;
}

export function switchPriority(bidId, bidIdToSwitchWith) {
  return () => {
    const baseUrl = 'preferences/bids/priority/switch';
    const url = buildUrlWithAdminImpersonation(baseUrl);
    const data = {
      bidId,
      bidIdToSwitchWith
    };
    return apiCall(url, 'put', data);
  };
}

export function updateScore(preference, newScore) {
  return () => {
    const baseUrl = 'preferences/bids/priority/update';
    const url = buildUrlWithAdminImpersonation(baseUrl);
    const data = {
      bidSessionId: preference.bidSessionId,
      staffId: preference.staffId,
      employeeBidId: preference.employeeBidId,
      bidAggregateId: preference.bidAggregateId,
      bidSessionSnapshotId: preference.bidSessionSnapshotId,
      preferenceOptionId: preference.preferenceOptionId,
      preferenceOptionPropertyId: preference.preferenceOptionPropertyId,
      score: newScore
    };
    return apiCall(url, 'put', data);
  };
}
export function updateWeight(preference, newScore, bidMode) {
  return () => {
    const baseUrl = `preferences/bids/weight/update/?bidMode=${bidMode}`;
    const url = buildUrlWithAdminImpersonation(baseUrl);
    const data = {
      bidSessionId: preference.bidSessionId,
      staffId: preference.staffId,
      employeeBidId: preference.employeeBidId,
      bidAggregateId: preference.bidAggregateId,
      bidSessionSnapshotId: preference.bidSessionSnapshotId,
      preferenceOptionId: preference.preferenceOptionId,
      preferenceOptionPropertyId: 0,
      score: newScore
    };
    return apiCall(url, 'put', data);
  };
}

export function updateOtherOption(otherOptionCode, data) {
  return () => {
    const baseUrl = `preferences/options/other/${otherOptionCode}`;
    const url = buildUrlWithAdminImpersonation(baseUrl);
    return apiCall(url, 'put', data);
  };
}

export function getPreferencesOptionsIds(data) {
  if (!data) return [];
  if (Object.keys(data).length === 0) return [];
  return data.entries.map(option => option.preferenceOptionId);
}

export function getNextAvailableLevel(
  bidPreferences,
  bidLevels,
  allowDuplicates
) {
  const maxReducer = (max, bidPreference) => Math.max(max, bidPreference.score);
  const computeNextLevel = maxScore => {
    return bidLevels.entries
      .map((level, index) => {
        if (level === maxScore) {
          if (allowDuplicates) {
            return maxScore;
          }
          if (index >= bidLevels.entries.length - 1) {
            return bidLevels.entries[bidLevels.entries.length - 1];
          }
          return bidLevels.entries[index + 1];
        }
      })
      .find(entry => entry !== undefined);
  };

  if (bidLevels === undefined || bidLevels.entries.length === 0) {
    return 'There is no configuration for bid ladder levels.';
  }

  if (bidPreferences === undefined || bidPreferences.entries.length === 0) {
    return bidLevels.entries[0];
  }

  const firstScore = bidPreferences.entries[0].score;
  const maxScore = bidPreferences.entries.reduce(maxReducer, firstScore);
  return computeNextLevel(maxScore);
}

export const fetchBidSession =
  (staffId, bidSessionId, applyStandingBid) => async dispatch => {
    let user,
      openBids = null,
      otherOptions = null,
      filters = null,
      preferenceOptions = null,
      bidMode = `PRIORITY`,
      crewinterfacemode = null,
      activePreferenceOptionsIds = null,
      bidPreferences = {
        decisionMessage: null,
        end: 0,
        entries: [],
        errorMessage: null,
        message: null,
        start: 0,
        totalEntries: 0
      },
      currentBid = null,
      respBidPreferences = null,
      lastSavedData = { data: {} };
    dispatch(fetchBidSessionRequest());
    try {
      let data = await Promise.all([
        fetch(`api/employees/${staffId}`)
          .then(res => res.json())
          .then(json => {
            user = json;
          }),
        fetch(`api/filters`)
          .then(res => res.json())
          .then(json => {
            filters = json;
          }),
        fetch(`api/preferences/options/other`)
          .then(res => res.json())
          .then(json => {
            otherOptions = json;
          }),
        fetch(`api/preferences/bid-sessions`)
          .then(res => res.json())
          .then(json => {
            openBids = json;
            currentBid = openBids.entries.filter(
              bid => bid.bidSessionId === Number(bidSessionId)
            )[0];
          }),
        fetch(`api/preferences/options`)
          .then(res => res.json())
          .then(json => {
            preferenceOptions = json;
            activePreferenceOptionsIds =
              getPreferencesOptionsIds(preferenceOptions);
          }),
        fetch(`api/activities/crewinterface-mode`).then(res =>
          res.json().then(json => {
            crewinterfacemode = json;
          })
        ),
        fetch(buildUrlWithAdminImpersonation(`api/preferences/bid-session/${bidSessionId}/employee/${staffId}`))
          .then(res => res.json())
          .then(json => {
            bidPreferences =
              json.totalEntries > 0 ? orderPreferences(json) : bidPreferences;
          })
      ]);

      if (
        user !== null &&
        currentBid !== null &&
        lastSavedData !== null &&
        lastSavedData !== null &&
        otherOptions !== null &&
        filters !== null &&
        activePreferenceOptionsIds !== null &&
        bidMode !== null &&
        crewinterfacemode !== null
      ) {
        dispatch(
          fetchBidSessionSuccess({
            user,
            currentBid,
            bidPreferences,
            lastSavedData,
            otherOptions,
            filters,
            activePreferenceOptionsIds,
            bidMode,
            crewinterfacemode
          })
        );
      }
    } catch (error) {
      console.log(error);
    }
  };
